// src/layout/AppLayout/index.js
import React from 'react';
import { 
  Box, 
  Container, 
  useTheme, 
  useMediaQuery, 
  Drawer, 
  IconButton, 
  AppBar, 
  Toolbar, 
  BottomNavigation, 
  BottomNavigationAction, 
  Tooltip
} from '@mui/material';
import { 
  WbSunny as SunIcon, 
  NightsStay as MoonIcon 
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import DrawerContent from './DrawerContent';
import { Main } from './styles';
import { DRAWER_WIDTH, MENU_ITEMS } from './constants';
import { useThemeToggle } from '../../context/ThemeContext';
import AppLogo from './AppLogo';

const AppLayout = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeToggle();
  
  // Breakpoints
  const isCompact = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isExpanded = useMediaQuery(theme.breakpoints.up('md')); // >= 900px

  const activeIndex = MENU_ITEMS.findIndex(item => 
    location.pathname === item.path || 
    (item.path !== '/' && location.pathname.startsWith(item.path))
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      
      {/* Top AppBar for Mobile & Tablet viewports */}
      {!isExpanded && (
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AppLogo compact={true} />
            </Box>
            <IconButton 
              onClick={toggleTheme}
              sx={{ 
                color: mode === 'dark' ? '#fbbf24' : '#6366f1',
                backgroundColor: theme.palette.surfaceContainerHigh,
                border: `1px solid ${theme.palette.outlineVariant}`,
                '&:hover': {
                  backgroundColor: theme.palette.surfaceContainerHighest,
                }
              }}
              aria-label="Toggle light/dark theme"
            >
              {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Side Navigation for Tablet/Medium: Navigation Rail */}
      {isMedium && (
        <Box
          component="nav"
          sx={{
            width: 80,
            flexShrink: 0,
            position: 'fixed',
            left: 0,
            top: 56, // below Mobile AppBar
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            zIndex: theme.zIndex.drawer,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', alignItems: 'center' }}>
            {MENU_ITEMS.map((item, idx) => {
              const active = idx === activeIndex;
              return (
                <Tooltip key={item.text} title={item.text} placement="right">
                  <IconButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      backgroundColor: active ? theme.palette.primaryContainer : 'transparent',
                      color: active ? theme.palette.onPrimaryContainer : theme.palette.text.secondary,
                      transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                      '&:hover': {
                        backgroundColor: active ? theme.palette.primaryContainer : theme.palette.surfaceContainerHigh,
                      },
                    }}
                  >
                    {item.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Side Navigation for Desktop: Permanent Navigation Drawer */}
      {isExpanded && (
        <Box
          component="nav"
          sx={{ width: DRAWER_WIDTH, flexShrink: 0 }}
          aria-label="Main sidebar navigation"
        >
          <Drawer
            variant="permanent"
            sx={{
              display: 'block',
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: DRAWER_WIDTH,
              },
            }}
            open
          >
            <DrawerContent />
          </Drawer>
        </Box>
      )}

      {/* Main Content Layout Adjustments */}
      <Main 
        sx={{ 
          flexGrow: 1, 
          width: '100%',
          transition: 'all 0.2s ease',
          pl: isExpanded ? 0 : isMedium ? '80px' : 0,
          pt: !isExpanded ? '56px' : 0,
          pb: isCompact ? '80px' : 0,
        }}
      >
        <Container 
          maxWidth="xl" 
          sx={{ mt: 4, mb: 4 }}
          key={location.pathname}
          className="page-transition-enter"
        >
          {children}
        </Container>
      </Main>

      {/* Bottom Navigation for Mobile: Bottom Navigation Bar */}
      {isCompact && (
        <Box 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: theme.zIndex.drawer + 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <BottomNavigation
            value={activeIndex}
            onChange={(event, newValue) => {
              if (newValue >= 0 && newValue < MENU_ITEMS.length) {
                navigate(MENU_ITEMS[newValue].path);
              }
            }}
            showLabels
            sx={{
              height: 80, // M3 standard bottom nav height
              backgroundColor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                color: theme.palette.text.secondary,
                minWidth: 'auto',
                padding: '12px 0',
                transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                '&.Mui-selected': {
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  '& .MuiSvgIcon-root': {
                    backgroundColor: theme.palette.primaryContainer,
                    color: theme.palette.onPrimaryContainer,
                    borderRadius: '16px', // M3 active shape pill
                    padding: '4px 20px',
                  }
                },
                '& .MuiSvgIcon-root': {
                  transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                  borderRadius: '16px',
                  padding: '4px 12px',
                }
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: 500,
                marginTop: '4px',
                letterSpacing: '0.01em',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }
              }
            }}
          >
            {MENU_ITEMS.map((item) => (
              <BottomNavigationAction 
                key={item.text} 
                label={item.text} 
                icon={item.icon} 
              />
            ))}
          </BottomNavigation>
        </Box>
      )}

    </Box>
  );
};

export default AppLayout;